import { apiClient } from "./client";
import { RoomServiceMenu } from "../types/roomServiceMenu";
import { RoomServiceItem } from "../types/roomServiceItem";
import { RoomServiceMenuSchedule } from "../types/roomServiceMenuSchedule";
import { DayOfWeek } from "../types/restaurant"; // Assuming DayOfWeek might be used or needed by schedules

// Generic Paginated Response (if not already in a shared types file)
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Generic API Response Wrapper (if not already in a shared types file)
interface ApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}

// --------- Room Service Menu ---------

// API response structure from backend for RoomServiceMenu
interface ApiRoomServiceMenuResponse {
  roomservicemenuid: string;
  hotelid: string;
  menuname: string;
  description: string | null;
  isactive: boolean;
  createdat: string;
  updatedat: string;
  // Any other fields the backend sends for a room service menu
}

// Helper function to transform API response to RoomServiceMenu
const transformRoomServiceMenu = (
  apiMenu: ApiRoomServiceMenuResponse
): RoomServiceMenu => {
  return {
    roomservicemenuid: apiMenu.roomservicemenuid,
    hotelid: apiMenu.hotelid,
    menuname: apiMenu.menuname,
    description: apiMenu.description,
    isactive: apiMenu.isactive,
    createdat: apiMenu.createdat,
    updatedat: apiMenu.updatedat,
  };
};

export const getRoomServiceMenus = async (
  hotelId: string
): Promise<RoomServiceMenu[]> => {
  if (!hotelId) {
    console.warn("Hotel ID is missing for getRoomServiceMenus");
    return [];
  }
  try {
    const response = await apiClient.get<
      ApiResponse<ApiRoomServiceMenuResponse[]>
    >(`/management/hotel/room-service-menus`);
    // Assuming backend wraps in { data: [...] }
    return response.data.data.map(transformRoomServiceMenu);
  } catch (error) {
    console.error("Error fetching room service menus:", error);
    throw error;
  }
};

export const createRoomServiceMenu = async (
  hotelId: string,
  menuData: Omit<
    RoomServiceMenu,
    "roomservicemenuid" | "hotelid" | "createdat" | "updatedat"
  >
): Promise<RoomServiceMenu> => {
  if (!hotelId) {
    throw new Error("Hotel ID is required to create a room service menu.");
  }
  try {
    const response = await apiClient.post<
      ApiResponse<ApiRoomServiceMenuResponse>
    >(
      `/management/hotel/room-service-menus`,
      { ...menuData, hotelid: hotelId } // Ensure hotelid is part of the payload if backend expects it
    );
    return transformRoomServiceMenu(response.data.data);
  } catch (error) {
    console.error("Error creating room service menu:", error);
    throw error;
  }
};

export const updateRoomServiceMenu = async (
  hotelId: string,
  menuId: string,
  menuData: Partial<
    Omit<RoomServiceMenu, "hotelid" | "createdat" | "updatedat">
  >
): Promise<RoomServiceMenu> => {
  if (!hotelId || !menuId) {
    throw new Error(
      "Hotel ID and Menu ID are required to update a room service menu."
    );
  }
  try {
    const response = await apiClient.put<
      ApiResponse<ApiRoomServiceMenuResponse>
    >(`/management/hotel/room-service-menus/${menuId}`, menuData);
    return transformRoomServiceMenu(response.data.data);
  } catch (error) {
    console.error("Error updating room service menu:", error);
    throw error;
  }
};

export const deleteRoomServiceMenu = async (
  hotelId: string,
  menuId: string
): Promise<void> => {
  if (!hotelId || !menuId) {
    throw new Error(
      "Hotel ID and Menu ID are required to delete a room service menu."
    );
  }
  try {
    await apiClient.delete(`/management/hotel/room-service-menus/${menuId}`);
  } catch (error) {
    console.error("Error deleting room service menu:", error);
    throw error;
  }
};

// Placeholder for Room Service Item functions

// --------- Room Service Item ---------

// API response structure from backend for RoomServiceItem
// Based on the actual backend response from hotel.ts
interface ApiRoomServiceItemResponse {
  rsItemId: string;
  menuId: string;
  itemName: string;
  description: string | null;
  ingredients: string | null;
  category: string | null;
  photo: string | null;
  price: {
    priceId: string;
    amount: string;
    currencyCode: string;
    priceType: string;
    description: string;
  };
  isSpecial: boolean;
  specialStartDate: string | null;
  specialEndDate: string | null;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

// Helper function to transform API response to RoomServiceItem
const transformRoomServiceItem = (
  apiItem: ApiRoomServiceItemResponse
): RoomServiceItem => {
  let parsedPriceAmount = 0;
  if (apiItem.price?.amount !== null && apiItem.price?.amount !== undefined) {
    const parsed = parseFloat(apiItem.price.amount);
    parsedPriceAmount = isNaN(parsed) ? 0 : parsed;
  }

  return {
    rsitemid: apiItem.rsItemId,
    hotelid: "",
    roomservicemenuid: apiItem.menuId,
    itemname: apiItem.itemName,
    description: apiItem.description,
    ingredients: apiItem.ingredients || "",
    category: apiItem.category,
    photo: apiItem.photo,
    priceAmount: parsedPriceAmount,
    currencyCode: apiItem.price?.currencyCode || "USD",
    isavailable: apiItem.isAvailable,
    createdat: apiItem.createdAt,
    updatedat: apiItem.updatedAt,
    price: apiItem.price?.priceId || "",
    spicelevel: "",
    resolvedPrice: apiItem.price
      ? {
          ...apiItem.price,
          amount: parseFloat(apiItem.price.amount),
        }
      : null,
    isSpecial: false,
    specialStartDate: null,
    specialEndDate: null,
  };
};

export const getRoomServiceItems = async (
  hotelId: string,
  menuId: string,
  page: number = 1,
  limit: number = 20,
  category?: string
): Promise<PaginatedResponse<RoomServiceItem>> => {
  if (!hotelId || !menuId) {
    throw new Error(
      "Hotel ID and Menu ID are required to fetch room service items."
    );
  }
  try {
    const response = await apiClient.get<
      ApiResponse<ApiRoomServiceItemResponse[]>
    >(`/management/hotel/room-service-menus/${menuId}/items`, {
      params: {
        page,
        limit,
        ...(category && { category }),
      },
    });
    const apiItems = response.data.data;
    return {
      data: apiItems.map(transformRoomServiceItem),
      pagination: {
        page,
        limit,
        total: apiItems.length, // Adjust if backend provides actual total
        totalPages: Math.ceil(apiItems.length / limit), // Adjust accordingly
      },
    };
  } catch (error) {
    console.error("Error fetching room service items:", error);
    throw error;
  }
};

export const createRoomServiceItem = async (
  hotelId: string,
  menuId: string,
  itemData: Omit<
    RoomServiceItem,
    | "rsitemid"
    | "hotelid"
    | "roomservicemenuid"
    | "createdat"
    | "updatedat"
    | "priceId" // priceId is not sent for creation
  >
): Promise<RoomServiceItem> => {
  if (!hotelId || !menuId) {
    throw new Error(
      "Hotel ID and Menu ID are required to create a room service item."
    );
  }
  // Transform frontend camelCase fields to backend lowercase fields
  const payload = {
    itemname: itemData.itemname,
    description: itemData.description,
    ingredients: itemData.ingredients,
    category: itemData.category,
    photo: itemData.photo,
    priceid: itemData.price, // Map 'price' to 'priceid'
    isactive: itemData.isavailable !== undefined ? itemData.isavailable : true,
  };

  try {
    const response = await apiClient.post<
      ApiResponse<ApiRoomServiceItemResponse>
    >(`/management/hotel/room-service-menus/${menuId}/items`, payload);
    return transformRoomServiceItem(response.data.data);
  } catch (error) {
    console.error("Error creating room service item:", error);
    throw error;
  }
};

export const updateRoomServiceItem = async (
  hotelId: string,
  menuId: string,
  itemId: string,
  itemData: Partial<
    Omit<
      RoomServiceItem,
      "hotelid" | "roomservicemenuid" | "createdat" | "updatedat"
    >
  >
): Promise<RoomServiceItem> => {
  if (!hotelId || !menuId || !itemId) {
    throw new Error(
      "Hotel ID, Menu ID, and Item ID are required to update a room service item."
    );
  }

  // Transform frontend camelCase fields to backend lowercase fields
  const payload: Record<string, any> = {};

  if (itemData.itemname !== undefined) payload.itemname = itemData.itemname;
  if (itemData.description !== undefined)
    payload.description = itemData.description;
  if (itemData.ingredients !== undefined)
    payload.ingredients = itemData.ingredients;
  if (itemData.category !== undefined) payload.category = itemData.category;
  if (itemData.photo !== undefined) payload.photo = itemData.photo;
  if (itemData.price !== undefined) payload.priceid = itemData.price;

  if (itemData.isavailable !== undefined)
    payload.isactive = itemData.isavailable;

  // Log the actual payload being sent

  try {
    const response = await apiClient.put<
      ApiResponse<ApiRoomServiceItemResponse> // Backend response type
    >(
      `/management/hotel/room-service-menus/${menuId}/items/${itemId}`,
      payload // Send the transformed payload
    );
    return transformRoomServiceItem(response.data.data); // Transform backend response to frontend type
  } catch (error) {
    console.error("Error updating room service item:", error);
    throw error;
  }
};

// Custom error class for FK reference errors
export class FKReferenceError extends Error {
  constructor(
    message: string,
    public orderCount: number,
    public itemName: string
  ) {
    super(message);
    this.name = "FKReferenceError";
  }
}

export const deleteRoomServiceItem = async (
  hotelId: string,
  menuId: string,
  itemId: string
): Promise<void> => {
  if (!hotelId || !menuId || !itemId) {
    throw new Error(
      "Hotel ID, Menu ID, and Item ID are required to delete a room service item."
    );
  }
  try {
    await apiClient.delete(
      `/management/hotel/room-service-menus/${menuId}/items/${itemId}`
    );
  } catch (error: any) {
    console.error("Error deleting room service item:", error);

    // Check if this is a FK reference error from the backend
    const errorMessage =
      error?.response?.data?.error?.message || error?.message || "";

    // Check if error message indicates FK reference (more flexible matching)
    if (
      errorMessage.includes("Cannot delete room service item") &&
      errorMessage.includes("referenced in") &&
      errorMessage.includes("existing dining order")
    ) {
      // Extract the count from the error message
      const match = errorMessage.match(
        /referenced in (\d+) existing dining order/
      );
      const orderCount = match ? parseInt(match[1], 10) : 0;

      throw new FKReferenceError(
        errorMessage,
        orderCount,
        "" // itemName will be set by the caller
      );
    }

    throw error;
  }
};

// Placeholder for RoomServiceMenuSchedule functions

// --------- Room Service Menu Schedules ---------

// API response structure from backend for RoomServiceMenuSchedule
// This includes fields from 'roomservicemenuschedule' table and potentially joined fields
// from 'scheduleinterval' like startTime and endTime.
interface ApiRoomServiceMenuScheduleResponse {
  scheduleId: string; // As sent by backend
  menuId: string; // As sent by backend
  intervalId: string; // As sent by backend
  dayOfWeek: string; // As sent by backend
  isActive: boolean; // As sent by backend
  startTime?: string | null; // As sent by backend (may be camelCase)
  endTime?: string | null; // As sent by backend (may be camelCase)
  starttime?: string | null; // Backend may return snake_case
  endtime?: string | null; // Backend may return snake_case
  intervalDescription?: string | null; // As sent by backend
  intervaldescription?: string | null; // Backend may return snake_case
  // Optional fields not in the provided GET list response but potentially used elsewhere
  scheduletype?: string | null;
  notes?: string | null;
  createdat?: string; // Corrected from createdAt
  updatedat?: string; // Corrected from updatedAt
}

// Helper function to transform API response to RoomServiceMenuSchedule
export const transformRoomServiceMenuSchedule = (
  apiSchedule: ApiRoomServiceMenuScheduleResponse | any
): RoomServiceMenuSchedule => {
  // Backend returns starttime and endtime at top level (from joined scheduleinterval table)
  // Handle both camelCase and snake_case from backend
  return {
    rsMenuscheduleid: apiSchedule.scheduleId, // Map scheduleId to rsMenuscheduleid
    roomServiceMenuId: apiSchedule.menuId, // Map menuId to roomServiceMenuId
    intervalid: apiSchedule.intervalId, // Map intervalId to intervalid
    dayOfWeek: apiSchedule.dayOfWeek, // Map dayOfWeek to dayOfWeek
    scheduletype: apiSchedule.scheduletype || null, // Optional field
    isActive: apiSchedule.isActive, // Map isActive to isActive
    notes: apiSchedule.notes || null, // Optional field
    startTime: apiSchedule.startTime || apiSchedule.starttime || null, // Handle both camelCase and snake_case
    endTime: apiSchedule.endTime || apiSchedule.endtime || null, // Handle both camelCase and snake_case
    intervalDescription: apiSchedule.intervalDescription || apiSchedule.intervaldescription || null,
    updatedat: apiSchedule.updatedat || apiSchedule.updatedAt || undefined, // Include updated timestamp for sorting
  };
};

export const getRoomServiceMenuSchedules = async (
  hotelId: string,
  roomServiceMenuId: string
): Promise<RoomServiceMenuSchedule[]> => {
  if (!hotelId || !roomServiceMenuId) {
    throw new Error(
      "Hotel ID and Room Service Menu ID are required to fetch schedules."
    );
  }
  try {
    const response = await apiClient.get<
      ApiResponse<ApiRoomServiceMenuScheduleResponse[]>
    >(`/management/hotel/room-service-menus/${roomServiceMenuId}/schedules`);
    return response.data.data.map(transformRoomServiceMenuSchedule);
  } catch (error) {
    console.error("Error fetching room service menu schedules:", error);
    throw error;
  }
};

// Type for creating a new schedule entry. We send intervalid, not startTime/endTime directly.
// Backend will link to scheduleinterval using intervalid.
interface CreateRoomServiceMenuSchedulePayload {
  intervalid: string;
  dayofweek: DayOfWeek | string;
  isactive: boolean;
  scheduletype?: string | null;
  notes?: string | null;
  // roomservicemenuid and hotelid are path params
}

export const createRoomServiceMenuScheduleEntry = async (
  hotelId: string,
  roomServiceMenuId: string,
  scheduleData: CreateRoomServiceMenuSchedulePayload
): Promise<RoomServiceMenuSchedule> => {
  if (!hotelId || !roomServiceMenuId) {
    throw new Error(
      "Hotel ID and Room Service Menu ID are required to create a schedule entry."
    );
  }
  try {
    const response = await apiClient.post<
      ApiResponse<ApiRoomServiceMenuScheduleResponse> // Expecting the created schedule back
    >(
      `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules`,
      scheduleData
    );
    return transformRoomServiceMenuSchedule(response.data.data);
  } catch (error) {
    console.error("Error creating room service menu schedule entry:", error);
    throw error;
  }
};

// Type for updating an existing schedule entry.
// We can update intervalid, or other properties like isactive, notes.
interface UpdateRoomServiceMenuSchedulePayload {
  intervalid?: string; // Can change the interval
  dayofweek?: DayOfWeek | string; // Usually not changed, but possible
  isactive?: boolean;
  scheduletype?: string | null;
  notes?: string | null;
}

export const updateRoomServiceMenuSchedule = async (
  hotelId: string,
  roomServiceMenuId: string,
  scheduleId: string, // This is rsMenuscheduleid
  scheduleData: UpdateRoomServiceMenuSchedulePayload
): Promise<RoomServiceMenuSchedule> => {
  if (!hotelId || !roomServiceMenuId || !scheduleId) {
    throw new Error(
      "Hotel ID, Room Service Menu ID, and Schedule ID are required to update a schedule entry."
    );
  }
  try {
    const response = await apiClient.put<
      ApiResponse<ApiRoomServiceMenuScheduleResponse> // Expecting the updated schedule back
    >(
      `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules/${scheduleId}`,
      scheduleData
    );
    return transformRoomServiceMenuSchedule(response.data.data);
  } catch (error) {
    console.error("Error updating room service menu schedule entry:", error);
    throw error;
  }
};

// Deleting a specific schedule entry (DELETE /schedules/:scheduleId)
export const deleteRoomServiceMenuSchedule = async (
  hotelId: string,
  roomServiceMenuId: string,
  scheduleId: string // This is rsMenuscheduleid
): Promise<void> => {
  if (!hotelId || !roomServiceMenuId || !scheduleId) {
    throw new Error(
      "Hotel ID, Room Service Menu ID, and Schedule ID are required to delete a schedule entry."
    );
  }
  try {
    await apiClient.delete(
      `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules/${scheduleId}`
    );
  } catch (error) {
    console.error("Error deleting room service menu schedule entry:", error);
    throw error;
  }
};
